import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { useTranslation } from '@disco/i18n/use-translation'
import captureError from '@disco/utils/captureError'
import {
  checkPasswordForBreaches,
  checkPasswordSecurity,
} from '@disco/utils/password'
import { validateEmail } from '@disco/utils/validation'

import { Box, Flex, Heading, PasswordTextField, TextField } from 'components/ds'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'

import Label from '../label'
import Separator from '../separator'
import type { SignUpDetails } from '../types'
import Warning from '../warning'
import ExplainError from './explain-error'
import Terms from './terms'

const PASSWORD_STRENGTH = 'standard'
type Props = {
  initialEmail?: string
  onSignupUser: (details: SignUpDetails) => Promise<{
    status: string
  }>
  onBackToLogin: () => void
}

type FormValues = {
  firstName: string
  lastName: string
  email: string
  newPassword: string
  passwordConfirm: string
}

/**
 * SignupForm component for creating new user accounts
 *
 * Provides a signup form with email, password, and password confirmation fields.
 * Includes password strength validation, breach checking, and error handling.
 * Allows users to create a free DISCO Catalog account.
 *
 * @param props - Component props
 * @param props.initialEmail - Pre-filled email address (optional)
 * @param props.onSignupUser - Callback function to handle user signup with email and password
 * @param props.onBackToLogin - Callback function to navigate back to login form
 * @returns JSX element containing the signup form
 */
const SignupForm = ({
  initialEmail,
  onSignupUser,
  onBackToLogin,
}: Props): React.ReactElement<'form'> => {
  const { t } = useTranslation('accounts')
  const [error, setError] = useState<string | null>(null)
  const {
    control,
    handleSubmit,
    watch,
    trigger,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: initialEmail || '',
      newPassword: '',
      passwordConfirm: '',
    },
  })

  const newPasswordValue = watch('newPassword')

  // The confirm-password field only cross-checks against newPassword when it
  // validates, so editing newPassword after a mismatch is shown doesn't
  // otherwise re-trigger it. Skip while confirm is untouched to avoid
  // flashing a mismatch before the user has even reached that field.
  useEffect(() => {
    if (getValues('passwordConfirm')) {
      trigger('passwordConfirm')
    }
  }, [newPasswordValue, trigger, getValues])

  // reValidateMode only kicks in after a submit; before that, mode: 'onBlur'
  // won't re-check a field as the user types, so a shown error would
  // otherwise persist until the next blur even after being fixed.
  const revalidateIfErrored = (field: keyof FormValues) => {
    if (errors[field]) {
      trigger(field)
    }
  }

  const onValid = async (data: FormValues) => {
    setError(null)

    try {
      const numBreached = await checkPasswordForBreaches(data.newPassword)

      if (numBreached) {
        setError('breached_password')
        return
      }
    } catch (err) {
      console.error('checkPasswordForBreaches error', err) // ignore failures

      captureError(err)
    }

    try {
      let res = await onSignupUser({
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        email: data.email.trim(),
        password: data.newPassword,
      })
      console.log('[signup] response', res)

      if (res.status !== 'success') {
        setError(res.status)
      }
    } catch (err) {
      console.error('[signup] error', err)
      setError(err.res)
    }
  }

  const displayedError =
    errors.firstName?.message ||
    errors.lastName?.message ||
    errors.email?.message ||
    errors.newPassword?.message ||
    errors.passwordConfirm?.message ||
    error

  return (
    <Flex asChild direction="column" gap="3">
      <form onSubmit={handleSubmit(onValid)}>
        <Heading align="center" mt="0">
          {t(
            'accounts:createAFreeDiscoCatalogAccountHeading',
            'Create a free DISCO Catalog account',
          )}
        </Heading>
        <Box>
          <Label
            htmlFor="firstName"
            caption={t('accounts:firstName', 'First name')}
            required
          />
          <Controller
            name="firstName"
            control={control}
            rules={{
              validate: value =>
                !!value.trim() ||
                t('accounts:firstNameIsRequired', 'First name is required.'),
            }}
            render={({ field: { onChange, onBlur, value, name } }) => (
              <TextField.Root
                size="3"
                id="firstName"
                name={name}
                type="text"
                autoComplete="given-name"
                aria-required="true"
                disabled={isSubmitting}
                value={value}
                onChange={e => {
                  onChange(e)
                  setError(null)
                  revalidateIfErrored('firstName')
                }}
                onBlur={onBlur}
                placeholder={t('accounts:firstName', 'First name')}
              />
            )}
          />
        </Box>
        <Box>
          <Label
            htmlFor="lastName"
            caption={t('accounts:lastName', 'Last name')}
            required
          />
          <Controller
            name="lastName"
            control={control}
            rules={{
              validate: value =>
                !!value.trim() ||
                t('accounts:lastNameIsRequired', 'Last name is required.'),
            }}
            render={({ field: { onChange, onBlur, value, name } }) => (
              <TextField.Root
                size="3"
                id="lastName"
                name={name}
                type="text"
                autoComplete="family-name"
                aria-required="true"
                disabled={isSubmitting}
                value={value}
                onChange={e => {
                  onChange(e)
                  setError(null)
                  revalidateIfErrored('lastName')
                }}
                onBlur={onBlur}
                placeholder={t('accounts:lastName', 'Last name')}
              />
            )}
          />
        </Box>
        <Box>
          <Label
            htmlFor="email"
            caption={t('accounts:yourEmail', 'Your email')}
            required
          />
          <Controller
            name="email"
            control={control}
            rules={{
              validate: value =>
                validateEmail(value.trim()) ||
                t(
                  'accounts:pleaseEnterAValidEmailAddress',
                  'Please enter a valid email address.',
                ),
            }}
            render={({ field: { onChange, onBlur, value, name } }) => (
              <TextField.Root
                size="3"
                id="email"
                name={name}
                type="email"
                autoComplete="email"
                aria-required="true"
                disabled={!!initialEmail || isSubmitting}
                value={value}
                onChange={e => {
                  onChange(e)
                  setError(null)
                  revalidateIfErrored('email')
                }}
                onBlur={onBlur}
                placeholder="you@company.com"
                autoCapitalize="off"
              />
            )}
          />
        </Box>
        <Box>
          <Label
            htmlFor="newPassword"
            caption={t('accounts:createPassword', 'Create password')}
            required
          />
          <Controller
            name="newPassword"
            control={control}
            rules={{
              validate: value =>
                checkPasswordSecurity(value, PASSWORD_STRENGTH) ||
                'WEAK_PASSWORD',
            }}
            render={({ field: { onChange, onBlur, value, name } }) => (
              <PasswordTextField
                size="3"
                id="newPassword"
                name={name}
                type="password"
                autoComplete="new-password"
                aria-required="true"
                value={value}
                onChange={e => {
                  onChange(e)
                  setError(null)
                  revalidateIfErrored('newPassword')
                }}
                onBlur={onBlur}
                placeholder={t('accounts:chooseAPassword', 'Choose a password')}
                strength={PASSWORD_STRENGTH}
                disabled={isSubmitting}
              />
            )}
          />
        </Box>

        <Box>
          <Label
            htmlFor="password"
            caption={t('accounts:repeatPassword', 'Repeat password')}
            required
          />
          <Controller
            name="passwordConfirm"
            control={control}
            rules={{
              validate: (value, formValues) =>
                value === formValues.newPassword ||
                t(
                  'accounts:passwordConfirmationDoesNotMatch',
                  'The password confirmation does not match.',
                ),
            }}
            render={({ field: { onChange, onBlur, value, name } }) => (
              <PasswordTextField
                size="3"
                id="password"
                name={name}
                type="password"
                autoComplete="new-password"
                aria-required="true"
                value={value}
                onChange={e => {
                  onChange(e)
                  setError(null)
                  revalidateIfErrored('passwordConfirm')
                }}
                onBlur={onBlur}
                placeholder={t('accounts:chooseAPassword', 'Choose a password')}
                disabled={isSubmitting}
              />
            )}
          />
        </Box>

        <ButtonContainer vertical>
          <Button
            appearance="primary"
            type="submit"
            large
            caption={t('accounts:createAccount', 'Create account')}
            progressIndicator={isSubmitting}
            progressCaption={t(
              'accounts:creatingAccountProgress',
              'Creating account',
            )}
            fullWidth
            textCenter
          />
          {displayedError && (
            <Warning>
              <ExplainError
                onBackToLogin={onBackToLogin}
                error={displayedError}
              />
            </Warning>
          )}
          <Button
            caption={t(
              'accounts:signInWithDiscoAccount',
              'Sign in with DISCO account',
            )}
            onClick={onBackToLogin}
            large
            fullWidth
            textCenter
          />
          <Separator />
          <Terms />
        </ButtonContainer>
      </form>
    </Flex>
  )
}

export default SignupForm
